import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { quizzesAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Progress } from '../components/ui/progress';
import { 
  Plus, 
  FileQuestion, 
  Play, 
  Trophy,
  Clock,
  CheckCircle,
  XCircle,
  Trash2,
  Edit2,
  ArrowRight,
  ArrowLeft,
  Save
} from 'lucide-react';
import { toast } from 'sonner';

const Quizzes = () => {
  const { canManage } = useAuth();
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQuizBuilder, setShowQuizBuilder] = useState(false);
  const [showQuizTaker, setShowQuizTaker] = useState(false);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [myAttempts, setMyAttempts] = useState([]);

  // Quiz Builder State
  const [quizForm, setQuizForm] = useState({
    title: '',
    description: '',
    passing_score: 70,
    max_retakes: 3,
    time_limit_minutes: null,
    questions: [],
  });

  // Quiz Taking State
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [quizzesRes, attemptsRes] = await Promise.all([
        quizzesAPI.getAll(),
        quizzesAPI.getMyAttempts()
      ]);
      setQuizzes(quizzesRes.data);
      setMyAttempts(attemptsRes.data);
    } catch (error) {
      console.error('Failed to load quizzes:', error);
      toast.error('Failed to load quizzes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateQuiz = () => {
    setSelectedQuiz(null);
    setQuizForm({
      title: '',
      description: '',
      passing_score: 70,
      max_retakes: 3,
      time_limit_minutes: null,
      questions: [],
    });
    setShowQuizBuilder(true);
  };

  const handleEditQuiz = (quiz) => {
    setSelectedQuiz(quiz);
    setQuizForm({
      title: quiz.title,
      description: quiz.description || '',
      passing_score: quiz.passing_score,
      max_retakes: quiz.max_retakes,
      time_limit_minutes: quiz.time_limit_minutes,
      questions: quiz.questions || [],
    });
    setShowQuizBuilder(true);
  };

  const handleAddQuestion = () => {
    setQuizForm({
      ...quizForm,
      questions: [
        ...quizForm.questions,
        {
          question_type: 'multiple_choice',
          question_text: '',
          options: ['', '', '', ''],
          correct_answer: '',
          points: 10,
        },
      ],
    });
  };

  const handleQuestionChange = (index, field, value) => {
    const newQuestions = [...quizForm.questions];
    newQuestions[index] = { ...newQuestions[index], [field]: value };
    setQuizForm({ ...quizForm, questions: newQuestions });
  };

  const handleOptionChange = (qIndex, oIndex, value) => {
    const newQuestions = [...quizForm.questions];
    newQuestions[qIndex].options[oIndex] = value;
    setQuizForm({ ...quizForm, questions: newQuestions });
  };

  const handleRemoveQuestion = (index) => {
    const newQuestions = quizForm.questions.filter((_, i) => i !== index);
    setQuizForm({ ...quizForm, questions: newQuestions });
  };

  const handleSaveQuiz = async () => {
    if (!quizForm.title.trim()) {
      toast.error('Please enter a quiz title');
      return;
    }
    if (quizForm.questions.length === 0) {
      toast.error('Please add at least one question');
      return;
    }

    try {
      if (selectedQuiz) {
        await quizzesAPI.update(selectedQuiz.id, quizForm);
        toast.success('Quiz updated');
      } else {
        await quizzesAPI.create(quizForm);
        toast.success('Quiz created');
      }
      setShowQuizBuilder(false);
      loadData();
    } catch (error) {
      console.error('Failed to save quiz:', error);
      toast.error('Failed to save quiz');
    }
  };

  const handleDeleteQuiz = async (quizId) => {
    if (!window.confirm('Are you sure you want to delete this quiz?')) return;
    
    try {
      await quizzesAPI.delete(quizId);
      toast.success('Quiz deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete quiz');
    }
  };

  const handleStartQuiz = (quiz) => {
    setSelectedQuiz(quiz);
    setCurrentQuestionIndex(0);
    setAnswers({});
    setQuizResult(null);
    setShowQuizTaker(true);
  };

  const handleAnswerSelect = (questionId, answer) => {
    setAnswers({ ...answers, [questionId]: answer });
  };

  const handleSubmitQuiz = async () => {
    try {
      const formattedAnswers = Object.entries(answers).map(([question_id, answer]) => ({
        question_id,
        answer,
      }));
      
      const response = await quizzesAPI.submitAttempt(selectedQuiz.id, formattedAnswers);
      setQuizResult(response.data);
      loadData();
    } catch (error) {
      console.error('Failed to submit quiz:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit quiz');
    }
  };

  const getQuizAttempts = (quizId) => {
    return myAttempts.filter(a => a.quiz_id === quizId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="quizzes-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 flex items-center gap-3">
            <FileQuestion className="w-8 h-8 text-blue-600" />
            Quizzes
          </h1>
          <p className="text-slate-600 mt-1">
            {canManage ? 'Create and manage training quizzes' : 'Take quizzes to test your knowledge'}
          </p>
        </div>
        {canManage && (
          <Button 
            onClick={handleCreateQuiz}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="create-quiz-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Quiz
          </Button>
        )}
      </div>

      {/* Quizzes Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {quizzes.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <FileQuestion className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No quizzes available</p>
            {canManage && (
              <Button onClick={handleCreateQuiz} className="mt-4">
                Create your first quiz
              </Button>
            )}
          </div>
        ) : (
          quizzes.map((quiz) => {
            const attempts = getQuizAttempts(quiz.id);
            const bestScore = attempts.length > 0 
              ? Math.max(...attempts.map(a => a.score)) 
              : null;
            const hasPassed = attempts.some(a => a.passed);

            return (
              <Card key={quiz.id} className="border-slate-200 shadow-card hover:shadow-float transition-all duration-300" data-testid={`quiz-card-${quiz.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-lg font-heading">{quiz.title}</CardTitle>
                    {hasPassed && (
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                        <Trophy className="w-3 h-3 mr-1" />
                        Passed
                      </Badge>
                    )}
                  </div>
                  {quiz.description && (
                    <CardDescription className="line-clamp-2">{quiz.description}</CardDescription>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-4 text-sm text-slate-600">
                    <span className="flex items-center gap-1">
                      <FileQuestion className="w-4 h-4" />
                      {quiz.questions?.length || 0} questions
                    </span>
                    <span className="flex items-center gap-1">
                      <Trophy className="w-4 h-4" />
                      {quiz.passing_score}% to pass
                    </span>
                  </div>

                  {bestScore !== null && (
                    <div>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-slate-600">Best Score</span>
                        <span className={`font-medium ${bestScore >= quiz.passing_score ? 'text-emerald-600' : 'text-amber-600'}`}>
                          {bestScore}%
                        </span>
                      </div>
                      <Progress value={bestScore} className="h-2" />
                    </div>
                  )}

                  <div className="flex gap-2 pt-2">
                    <Button 
                      onClick={() => handleStartQuiz(quiz)}
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                      disabled={attempts.length >= quiz.max_retakes && !hasPassed}
                      data-testid={`start-quiz-${quiz.id}`}
                    >
                      <Play className="w-4 h-4 mr-2" />
                      {attempts.length > 0 ? 'Retake' : 'Start'}
                    </Button>
                    {canManage && (
                      <>
                        <Button 
                          variant="outline" 
                          size="icon"
                          onClick={() => handleEditQuiz(quiz)}
                          data-testid={`edit-quiz-${quiz.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="icon"
                          onClick={() => handleDeleteQuiz(quiz.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-quiz-${quiz.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>

                  {attempts.length >= quiz.max_retakes && !hasPassed && (
                    <p className="text-xs text-red-500 text-center">
                      Maximum retakes reached
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Quiz Builder Dialog */}
      <Dialog open={showQuizBuilder} onOpenChange={setShowQuizBuilder}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {selectedQuiz ? 'Edit Quiz' : 'Create Quiz'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label>Quiz Title</Label>
                <Input
                  value={quizForm.title}
                  onChange={(e) => setQuizForm({ ...quizForm, title: e.target.value })}
                  placeholder="e.g., Study Startup Fundamentals"
                  data-testid="quiz-title-input"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={quizForm.description}
                  onChange={(e) => setQuizForm({ ...quizForm, description: e.target.value })}
                  placeholder="Brief description of this quiz"
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label>Passing Score (%)</Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={quizForm.passing_score}
                  onChange={(e) => setQuizForm({ ...quizForm, passing_score: parseInt(e.target.value) })}
                />
              </div>
              <div className="space-y-2">
                <Label>Max Retakes</Label>
                <Input
                  type="number"
                  min={1}
                  value={quizForm.max_retakes}
                  onChange={(e) => setQuizForm({ ...quizForm, max_retakes: parseInt(e.target.value) })}
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900">Questions</h3>
                <Button onClick={handleAddQuestion} variant="outline" size="sm" data-testid="add-question-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  Add Question
                </Button>
              </div>

              <div className="space-y-4">
                {quizForm.questions.map((question, qIndex) => (
                  <Card key={qIndex} className="border-slate-200">
                    <CardContent className="pt-4 space-y-4">
                      <div className="flex items-start justify-between">
                        <span className="text-sm font-medium text-slate-500">Question {qIndex + 1}</span>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleRemoveQuestion(qIndex)}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Type</Label>
                          <Select 
                            value={question.question_type}
                            onValueChange={(v) => handleQuestionChange(qIndex, 'question_type', v)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="multiple_choice">Multiple Choice</SelectItem>
                              <SelectItem value="true_false">True/False</SelectItem>
                              <SelectItem value="short_answer">Short Answer</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Points</Label>
                          <Input
                            type="number"
                            min={1}
                            value={question.points}
                            onChange={(e) => handleQuestionChange(qIndex, 'points', parseInt(e.target.value))}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Question Text</Label>
                        <Textarea
                          value={question.question_text}
                          onChange={(e) => handleQuestionChange(qIndex, 'question_text', e.target.value)}
                          placeholder="Enter your question..."
                          rows={2}
                        />
                      </div>

                      {question.question_type === 'multiple_choice' && (
                        <div className="space-y-2">
                          <Label>Options</Label>
                          {question.options.map((option, oIndex) => (
                            <Input
                              key={oIndex}
                              value={option}
                              onChange={(e) => handleOptionChange(qIndex, oIndex, e.target.value)}
                              placeholder={`Option ${oIndex + 1}`}
                              className="mb-2"
                            />
                          ))}
                        </div>
                      )}

                      {question.question_type === 'true_false' && (
                        <p className="text-sm text-slate-500">Options will be "True" and "False"</p>
                      )}

                      <div className="space-y-2">
                        <Label>Correct Answer</Label>
                        <Input
                          value={question.correct_answer}
                          onChange={(e) => handleQuestionChange(qIndex, 'correct_answer', e.target.value)}
                          placeholder={question.question_type === 'true_false' ? 'true or false' : 'Enter correct answer'}
                        />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuizBuilder(false)}>Cancel</Button>
            <Button onClick={handleSaveQuiz} className="bg-blue-600 hover:bg-blue-700" data-testid="save-quiz-btn">
              <Save className="w-4 h-4 mr-2" />
              Save Quiz
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quiz Taking Dialog */}
      <Dialog open={showQuizTaker} onOpenChange={setShowQuizTaker}>
        <DialogContent className="sm:max-w-[600px]">
          {selectedQuiz && !quizResult && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">{selectedQuiz.title}</DialogTitle>
                <div className="flex items-center gap-4 text-sm text-slate-500 pt-2">
                  <span>Question {currentQuestionIndex + 1} of {selectedQuiz.questions?.length}</span>
                  <Progress 
                    value={(currentQuestionIndex + 1) / (selectedQuiz.questions?.length || 1) * 100} 
                    className="flex-1 h-2"
                  />
                </div>
              </DialogHeader>
              
              {selectedQuiz.questions && selectedQuiz.questions[currentQuestionIndex] && (
                <div className="py-6">
                  <p className="text-lg font-medium text-slate-900 mb-6">
                    {selectedQuiz.questions[currentQuestionIndex].question_text}
                  </p>

                  {selectedQuiz.questions[currentQuestionIndex].question_type === 'multiple_choice' && (
                    <RadioGroup
                      value={answers[selectedQuiz.questions[currentQuestionIndex].id] || ''}
                      onValueChange={(value) => handleAnswerSelect(selectedQuiz.questions[currentQuestionIndex].id, value)}
                      className="space-y-3"
                    >
                      {selectedQuiz.questions[currentQuestionIndex].options?.map((option, index) => (
                        <div key={index} className="flex items-center space-x-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
                          <RadioGroupItem value={option} id={`option-${index}`} />
                          <Label htmlFor={`option-${index}`} className="flex-1 cursor-pointer">{option}</Label>
                        </div>
                      ))}
                    </RadioGroup>
                  )}

                  {selectedQuiz.questions[currentQuestionIndex].question_type === 'true_false' && (
                    <RadioGroup
                      value={answers[selectedQuiz.questions[currentQuestionIndex].id] || ''}
                      onValueChange={(value) => handleAnswerSelect(selectedQuiz.questions[currentQuestionIndex].id, value)}
                      className="space-y-3"
                    >
                      {['true', 'false'].map((option) => (
                        <div key={option} className="flex items-center space-x-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
                          <RadioGroupItem value={option} id={`option-${option}`} />
                          <Label htmlFor={`option-${option}`} className="flex-1 cursor-pointer capitalize">{option}</Label>
                        </div>
                      ))}
                    </RadioGroup>
                  )}

                  {selectedQuiz.questions[currentQuestionIndex].question_type === 'short_answer' && (
                    <Input
                      value={answers[selectedQuiz.questions[currentQuestionIndex].id] || ''}
                      onChange={(e) => handleAnswerSelect(selectedQuiz.questions[currentQuestionIndex].id, e.target.value)}
                      placeholder="Type your answer..."
                      className="h-12"
                    />
                  )}
                </div>
              )}

              <DialogFooter className="flex justify-between">
                <Button
                  variant="outline"
                  onClick={() => setCurrentQuestionIndex(Math.max(0, currentQuestionIndex - 1))}
                  disabled={currentQuestionIndex === 0}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Previous
                </Button>
                <div className="flex gap-2">
                  {currentQuestionIndex < (selectedQuiz.questions?.length || 0) - 1 ? (
                    <Button
                      onClick={() => setCurrentQuestionIndex(currentQuestionIndex + 1)}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      Next
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  ) : (
                    <Button
                      onClick={handleSubmitQuiz}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="submit-quiz-btn"
                    >
                      Submit Quiz
                      <CheckCircle className="w-4 h-4 ml-2" />
                    </Button>
                  )}
                </div>
              </DialogFooter>
            </>
          )}

          {quizResult && (
            <div className="text-center py-8">
              <div className={`w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center ${
                quizResult.passed ? 'bg-emerald-100' : 'bg-red-100'
              }`}>
                {quizResult.passed ? (
                  <Trophy className="w-10 h-10 text-emerald-600" />
                ) : (
                  <XCircle className="w-10 h-10 text-red-600" />
                )}
              </div>
              <h3 className="text-2xl font-heading font-bold text-slate-900 mb-2">
                {quizResult.passed ? 'Congratulations!' : 'Keep Trying!'}
              </h3>
              <p className="text-slate-600 mb-6">
                {quizResult.passed 
                  ? 'You have passed this quiz.'
                  : 'You did not meet the passing score this time.'
                }
              </p>
              <div className="text-5xl font-bold mb-2" style={{ color: quizResult.passed ? '#10B981' : '#EF4444' }}>
                {quizResult.score}%
              </div>
              <p className="text-slate-500">
                {quizResult.earned_points} / {quizResult.total_points} points
              </p>
              <Button 
                onClick={() => setShowQuizTaker(false)} 
                className="mt-6"
                data-testid="close-result-btn"
              >
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Quizzes;
